import { createDesign, saveDesign } from '../../../api/design';
import { setQueryParam } from '../../../utils/queryParams';
import { DEFAULT_DESIGN_ID } from '../_initialState';

export const createDesignActions = (set, get) => ({
  createDesign: async (host, designData = {}) => {
    try {
      const designId = await createDesign(host, 15, 'VehicleStorage');

      // Prepare design data for server
      const designToSave = {
        ...designData,
        id: designId,
        DesignName: designData?.DesignName || 'Vehicle Storage Design',
      };

      // Save the complete design to the server
      await saveDesign(host, 15, 'VehicleStorage', designToSave);

      // Find the temporary design in the IDs array
      const designIds = get().designIds;
      const tempIdIndex = designIds.indexOf(designData.Id);

      if (tempIdIndex !== -1) {
        // Replace temp ID with permanent ID while maintaining array order
        const newDesignIds = [...designIds];
        newDesignIds[tempIdIndex] = designId;

        // Update store with the permanent ID from the server
        get().updateVehicleVisualizer({
          key: 'vehicleStorage',
          actionName: 'createDesignSuccess',
          payload: {
            selectedDesignId: designId,
            designIds: newDesignIds,
            designs: {
              ...get().designs,
              [designId]: designToSave,
              [designData.Id]: undefined // Remove the temp design
            }
          },
        });

        // Update URL query param if needed
        setQueryParam('designid', designId);
      } else {
        // Fallback in case the temp ID isn't found

        get().updateVehicleVisualizer({
          key: 'vehicleStorage',
          actionName: 'createDesignSuccess',
          payload: {
            selectedDesignId: designId,
            designIds: [...get().designIds, designId],
            designs: {
              ...get().designs,
              [designId]: designToSave
            }
          },
        });

        // Update URL query param if needed
        setQueryParam('designid', designId);
      }

      return designId;
    } catch (error) {
      console.error('Error creating design with API:', error);
      return null;
    }
  },

  selectDesign: (designId) => {
    const designs = get().designs;

    if (designId && !designs[designId]) {
      console.warn(`Cannot select: Design with ID ${designId} not found.`);
      return false;
    }

    return true;
  },

  updateDesign: async (host, designId, designData) => {
    try {
      const existingDesign = get().designs[designId];

      if (!existingDesign) {
        console.warn(`Cannot update: Design with ID ${designId} not found.`);
        return false;
      }

      // Mark as updating in the UI first
      get().updateVehicleVisualizer({
        key: 'vehicleStorage',
        actionName: 'updateDesign',
        payload: {
          designs: {
            ...get().designs,
            [designId]: {
              ...get().designs[designId],
              ...designData,
            }
          }
        },
      });

      // Handle temporary designs that haven't been saved to the server yet
      if (designId.startsWith(DEFAULT_DESIGN_ID)) {
        // Convert this to a create operation instead
        return await get().createDesign(
          host,
          {
            ...existingDesign,
            ...designData
          }
        );
      }

      // Prepare the updated design
      const updatedDesign = {
        ...existingDesign,
        ...designData,
      };

      // Save to server
      await saveDesign(host, 15, 'VehicleStorage', updatedDesign);

      // Update local state to reflect success
      get().updateVehicleVisualizer({
        key: 'vehicleStorage',
        actionName: 'updateDesignSuccess',
        payload: {
          designs: {
            ...get().designs,
            [designId]: {
              ...updatedDesign,
            }
          }
        },
      });
      // Update URL query param if needed
      setQueryParam('designid', designId);
      return true;
    } catch (error) {
      console.error('Error updating design with API:', error);
      return false;
    }
  },

  deleteDesign: (designId) => {
    const currentState = get();
    const designToDelete = currentState.designs[designId];

    if (!designToDelete) {
      console.warn(`Cannot delete: Design with ID ${designId} not found.`);
      return false;
    }

    // Remove design from state
    const { [designId]: removedDesign, ...remainingDesigns } = currentState.designs;
    const newDesignIds = currentState.designIds.filter((id) => id !== designId);
    let newSelectedId = currentState.selectedDesignId;

    if (currentState.selectedDesignId === designId) {
      newSelectedId = newDesignIds.length > 0 ? newDesignIds[0] : null;

      // Update URL if changing selection
      if (newSelectedId) {
        setQueryParam('designid', newSelectedId);
      } else {
        setQueryParam('designid', null);
      }
    }

    get().updateVehicleVisualizer(set, get, {
      key: 'vehicleStorage',
      actionName: 'createDesignSuccess',
      payload: {
        designs: remainingDesigns,
        designIds: newDesignIds,
        selectedDesignId: newSelectedId
      },
    });

    return true;
  },
});
